'use client';

import type { Dayjs } from 'dayjs';

import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';

import { Iconify } from 'src/components/iconify';
import { Label } from 'src/components/label';

// ----------------------------------------------------------------------

export type Allvarlighetsgrad = 'INFO' | 'VARNING' | 'BRÅDSKANDE';

export type Åtgärdsrad = {
  id: string;
  rubrik: string;
  /** Kort förtydligande under rubriken — belopp, förfallodag, avsändare. */
  detalj?: string;
  /** Föreningen raden gäller. Utelämnad för personens egna uppgifter. */
  orgId?: string;
  orgNamn?: string;
  grad: Allvarlighetsgrad;
  /** Sorteringsnyckel. Rader utan förfallodag hamnar efter dem som har en. */
  förfallodag?: Dayjs;
  länk?: string;
  internLänk?: boolean;
};

type Props = {
  rader: Åtgärdsrad[];
  laddar?: boolean;
  /** Anropas med raden när användaren klickar. Hubben byter organisation och navigerar. */
  onÖppna: (rad: Åtgärdsrad) => void;
};

// ----------------------------------------------------------------------

/**
 * Hur många rader som visas innan resten läggs bakom knappen.
 *
 * Listan slår ihop varje förenings förfallodagar och blir lång — och eftersom den
 * sorteras på datum är det de redan passerade som hamnar överst. En vägg av rader
 * säger mindre än de närmaste fem: den som har något att göra ser det direkt, och
 * den som vill ha hela bilden är ett klick bort.
 */
const FÖRSTA_RADERNA = 5;

const GRADFÄRG: Record<Allvarlighetsgrad, 'error' | 'warning' | 'info'> = {
  BRÅDSKANDE: 'error',
  VARNING: 'warning',
  INFO: 'info',
};

/**
 * Rader sorteras på hur bråttom det är, inte på vilken förening de kommer från.
 * Det är hela poängen med hubben: den som är med i flera föreningar ska se en
 * lista i den ordning sakerna behöver göras, inte fem listor att jämföra.
 */
export function sorteraÅtgärder(rader: Åtgärdsrad[]): Åtgärdsrad[] {
  const rang: Record<Allvarlighetsgrad, number> = { BRÅDSKANDE: 0, VARNING: 1, INFO: 2 };
  return [...rader].sort((a, b) => {
    if (a.förfallodag && b.förfallodag) {
      const skillnad = a.förfallodag.valueOf() - b.förfallodag.valueOf();
      if (skillnad !== 0) return skillnad;
    } else if (a.förfallodag) {
      return -1;
    } else if (b.förfallodag) {
      return 1;
    }
    return rang[a.grad] - rang[b.grad];
  });
}

// ----------------------------------------------------------------------

export default function Åtgärdslista({ rader, laddar = false, onÖppna }: Props) {
  const [utvidgad, setUtvidgad] = useState(false);

  if (rader.length === 0) {
    return (
      <Card sx={{ px: 3, py: 4, borderRadius: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Iconify
            icon="solar:check-circle-bold-duotone"
            sx={{ width: 36, height: 36, color: 'success.main', flexShrink: 0 }}
          />
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {laddar ? 'Hämtar vad som väntar' : 'Inget väntar just nu'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {laddar
                ? 'Letar igenom dina föreningar.'
                : 'Nya förfallodagar och obokförda poster dyker upp här när de kommer.'}
            </Typography>
          </Box>
        </Stack>
      </Card>
    );
  }

  const synliga = utvidgad ? rader : rader.slice(0, FÖRSTA_RADERNA);
  const dolda = rader.length - synliga.length;

  return (
    <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
      <Stack divider={<Divider sx={{ borderStyle: 'dashed' }} />}>
        {synliga.map((rad) => {
          const färg = GRADFÄRG[rad.grad];

          return (
            <ButtonBase
              key={rad.id}
              onClick={() => onÖppna(rad)}
              sx={(theme) => ({
                px: 2.5,
                py: 1.75,
                gap: 2,
                width: '100%',
                textAlign: 'left',
                justifyContent: 'flex-start',
                // Kantmarkeringen bär allvarlighetsgraden. Den sitter till vänster om
                // texten och inte som en färgad bricka till höger, så att blicken kan
                // löpa nedför listan och se var det bränns utan att läsa varje rad.
                borderLeft: `3px solid ${theme.palette[färg].main}`,
                transition: theme.transitions.create('background-color', {
                  duration: theme.transitions.duration.shortest,
                }),
                '&:hover': { bgcolor: alpha(theme.palette[färg].main, 0.06) },
              })}
            >
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ flexWrap: 'wrap', gap: 0.75 }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {rad.rubrik}
                  </Typography>

                  {/*
                    Föreningens namn på raden är det som gör en sammanslagen lista
                    läsbar — utan det går det inte att se vilken förening en post
                    hör till. Personens egna uppgifter saknar förening och får
                    ingen bricka alls.
                  */}
                  {rad.orgNamn && (
                    <Label color="default" sx={{ height: 20 }}>
                      {rad.orgNamn}
                    </Label>
                  )}
                </Stack>

                {rad.detalj && (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {rad.detalj}
                  </Typography>
                )}
              </Box>

              <Iconify
                icon="eva:arrow-ios-forward-fill"
                width={18}
                sx={{ color: 'text.disabled', flexShrink: 0 }}
              />
            </ButtonBase>
          );
        })}
      </Stack>

      {(dolda > 0 || utvidgad) && (
        <>
          <Divider sx={{ borderStyle: 'dashed' }} />
          <ButtonBase
            onClick={() => setUtvidgad((tidigare) => !tidigare)}
            aria-expanded={utvidgad}
            sx={{
              px: 2.5,
              py: 1.5,
              gap: 0.5,
              width: '100%',
              color: 'text.secondary',
              justifyContent: 'center',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {utvidgad ? 'Visa färre' : `Visa ${dolda} till`}
            </Typography>
            <Iconify
              icon={utvidgad ? 'eva:arrow-ios-upward-fill' : 'eva:arrow-ios-downward-fill'}
              width={18}
            />
          </ButtonBase>
        </>
      )}
    </Card>
  );
}
